// Selectors over the UserMessage slice and the DB message/campaign stores.
// Kept apart from UserMessageReducer.ts (the slice) so that importing the slice's
// actions — e.g. from UserMessagesThunks — does not pull in WhatsAppSelectors, which
// imports back into the thunk graph (WhatsAppSelectors → DBServiceThunks → … →
// UserMessagesThunks): that cycle left getUserMessagesForPlanner undefined at load.
import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { createSelector } from "@reduxjs/toolkit";

import { campaignsSelectByQuery, userMessagesSelectByQuery } from "@common-reducers/DBServiceThunks";
import { countScheduleSlotsUsed, isOverScheduleLimit } from "./free-limit-gate";
import { toRuleSet } from "@common/components/schedule-picker/recurrence-utils";
import { CommonRootState } from "@common/types/common-root-state-type";
import { CampaignStatus } from '@common/models/campaign-model';
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import _ from 'lodash';
import moment from "moment";
import { getMessagesByCampaignId, getStuckMessagesByCampaignId } from "./CampaignSelectors";
import { entityMatchesSidebarSearch } from "./calendar-sidebar-search";
import { messageLimitSelector } from "./UserSelectors";
import { getUserMessagesForPlanner, selectWhatsappContactById } from "./WhatsAppSelectors";
import { sentMessageIdsFromLogs, excludeSentFromWaiting } from "../services/campaign-waiting-reconcile";
import {
  PlannerFilters,
  PERMISSIVE_PLANNER_FILTERS,
  campaignsVisible,
  isPlannerCandidate,
  normalizePlannerFilters,
  plannerSourceStatuses,
} from "../services/planner-filters";


export const getContactWithLastMessage = createSelector(
  [
    (state: CommonRootState) => state,
    (_state: CommonRootState, searchToken: string | undefined) => searchToken
  ],
  (state: CommonRootState, searchToken: string | undefined) => {

    // Check if a rule set has any future events after a given date
    const hasRemainingEvents = (ruleSetAsString: string | null, dueDate: string | null): boolean => {
      if (!dueDate) return false; // Default to false if no due date
      if (!ruleSetAsString) return moment.utc(dueDate).isAfter(moment.utc()); // Check due date only if no recurrence rules

      try {
        const now = moment.utc().startOf('minute').toDate();
        const parsedDueDate = moment.utc(dueDate).toDate();
        const ruleSet = toRuleSet(ruleSetAsString);

        // Check if dueDate is in the future
        if (parsedDueDate >= now) {
          // Future dueDate: Ensure recurrence rules do not exclude dueDate
          return ruleSet.between(parsedDueDate, parsedDueDate, true).length > 0 || ruleSet.after(now) !== null;
        }

        // Past dueDate: Check if there are future recurrence events
        return ruleSet.after(now) !== null;
      } catch (error) {
        console.error('Failed to parse rruleset in hasRemainingEvents:', ruleSetAsString, error);
        // Fall back to checking only the due date if rruleset parsing fails
        return moment.utc(dueDate).isAfter(moment.utc());
      }
    };


    const lowerCaseToken = searchToken?.toLowerCase();
    const messageList = userMessagesSelectByQuery(state.DBUserMessageReducer, {
      $or: [
        { deleted: { $exists: false } },
        { deleted: false }
      ],
      status: "pending"
    }, null, { dueDate: 1 })
      .filter(message => !message.campaignId)
      .filter(message => hasRemainingEvents(message.rruleset ?? null, message.dueDate?.toString() ?? ''))

    const groupedByWaId = _.groupBy(messageList, (message) => message?.contactList?.[0]?.id ?? '');

    let allMessages: UserMessage[] = []

    for (const [key, userMessages] of Object.entries(groupedByWaId)) {

      const apiCount = userMessages.filter(msg => msg.api).length;
      const campaignCount = userMessages.filter(msg => msg.campaignId).length;
      const scheduledCount = userMessages.length - apiCount - campaignCount;

      const message = userMessages[0];

      allMessages.push({
        ...message,
        contactWAId: message?.contactList?.[0]?.id ?? "",
        contactName: message?.contactList?.[0]?.name ?? message?.contactList?.[0]?.id ?? "",
        contactImageUrl: message?.contactList?.[0]?.imageUrl,
        messageCount: userMessages.length,

        messageScheduledCount: scheduledCount,
        messageApiCount: apiCount,
        messageCampaignCount: campaignCount,
      })
    }

    // let messageListGroupByPerson: UserMessage[] = []

    // messageList.forEach(message => {

    //   let isContactExist = false;

    //   message = {
    //     ...message,
    //     contactWAId: message?.contactList[0]?.id ?? "",
    //     contactName: message?.contactList[0]?.name ?? message?.contactList[0]?.id ?? "",
    //     contactImageUrl: message?.contactList[0]?.imageUrl,
    //     messageCount: 1,
    //   }

    //   messageListGroupByPerson.forEach(message2 => {

    //     if (message.contactWAId === message2.contactWAId) {
    //       message2.messageCount++
    //       isContactExist = true;
    //       return;
    //     }

    //   })

    //   if (!isContactExist) {
    //     messageListGroupByPerson.push(message)
    //   }

    // });


    return allMessages.filter(message => { // messageListGroupByPerson
      const whatsappContact = selectWhatsappContactById(state, message.contactWAId ?? '')

      return lowerCaseToken === undefined ||
        (whatsappContact?.name?.toLowerCase()?.indexOf(lowerCaseToken) ?? -1) > -1 ||
        (message.message?.toLowerCase()?.indexOf(lowerCaseToken) ?? -1) > -1;
    });

  }
);

// Types for better type safety
// Types for better type safety
type EntityType = 'message' | 'campaign';

type MessageWithType = {
  _id: string;
  type: 'message';
  dueDate?: string | Date;
  message?: string;
  isGroup?: boolean;
  // FE#128: recipient/group names so the sidebar search can match by name,
  // not only by message body. Sourced from the message's `contactList`.
  contactNames?: string[];
};

type CampaignWithType = {
  _id: string;
  type: 'campaign';
  campaignStatus: CampaignStatus;
  schedule?: {
    dueDate?: string;
  };
  templateName?: string;
  templateMessage?: string;
};

type CombinedEntity = MessageWithType | CampaignWithType;

// Memoized input selectors
const selectPlannerFilters = createSelector(
  (state: CommonRootState) => state.CalendarReducer?.calendarSidebarFilters,
  (raw): PlannerFilters => normalizePlannerFilters(raw),
);

// The sidebar selects and filters through the SAME shared predicate the calendar grid
// uses (@common/services/planner-filters). It used to hard-code `status $in
// ['pending','failed']` and apply the narrowing toggles by hand in the list component,
// which is how "Show what already happened" ended up doing nothing here while
// "Failed only" did nothing on the grid.
//
// It does not merely call the same predicate — it reads the SAME selected list the
// calendar reads (`getUserMessagesForPlanner`). This used to repeat that query and
// filter pass itself, and `selectPlannerCounts` repeated it a third time, so every
// message-slice change ran the planner predicate over every message three times to
// produce one answer.
const selectUserMessages = createSelector(
  getUserMessagesForPlanner,
  (messages): MessageWithType[] => messages.map(msg => ({
    _id: msg._id || '',
    type: 'message' as const,
    dueDate: msg.dueDate,
    message: typeof msg.message === 'string' ? msg.message : '',
    // Recipient is a WhatsApp group when its chat id ends with @g.us
    isGroup: chatKindOf(msg.contactList?.[0]?.id) === 'group',
    // FE#128: names of all recipients (contacts and groups) for name search
    contactNames: (msg.contactList ?? [])
      .map((c: any) => c?.name)
      .filter((n: any): n is string => typeof n === 'string' && n.length > 0),
  }))
);

const selectPendingOrRunningCampaigns = createSelector(
  (state: CommonRootState) => state.DBCampaignReducer,
  selectPlannerFilters,
  (campaignState, filters): CampaignWithType[] => (!campaignsVisible(filters) ? [] : campaignsSelectByQuery(campaignState, {
    campaignStatus: { $in: ['pending', 'running'] }
  })).map(campaign => ({
    _id: campaign._id || '',
    type: 'campaign' as const,
    campaignStatus: campaign.campaignStatus || 'pending',
    schedule: campaign.schedule,
    templateName: campaign?.template?.name || '',
    templateMessage: campaign?.template?.message || ''
  }))
);

const selectCalendarSidebarSearchToken = (_state: CommonRootState, searchToken?: string) => searchToken;

/** Parse a due date to epoch ms; `undefined` for missing/unparseable dates. */
const toDueMs = (date: string | Date | undefined): number | undefined => {
  if (!date) return undefined;
  const ms = new Date(date).getTime();
  return Number.isNaN(ms) ? undefined : ms;
};

// Main selector with optimized sorting
export const getAllEntitiesIdsAndTypeForCalendarSidebar = createSelector(
  selectUserMessages,
  selectPendingOrRunningCampaigns,
  selectCalendarSidebarSearchToken,
  (userMessages, campaigns, searchToken): Array<{ _id: string; type: EntityType; isGroup: boolean; dueDateMs?: number; isUpcoming: boolean }> => {
    const allEntities = [...userMessages, ...campaigns] as CombinedEntity[];
    // "Now" anchor for the sidebar ordering. Recomputed whenever the memoized
    // inputs change, which is often enough for a list of scheduled sends.
    const nowMs = Date.now();
    const normalizedSearchToken = (searchToken || '').trim().toLowerCase();

    // FE#128: match message text AND recipient/group names (and campaign
    // template name/message). See calendar-sidebar-search.ts.
    const filteredEntities = normalizedSearchToken
      ? allEntities.filter(entity => entityMatchesSidebarSearch(entity, normalizedSearchToken))
      : allEntities;

    const dueMsOf = (e: CombinedEntity) =>
      toDueMs(e.type === 'campaign' ? e.schedule?.dueDate : e.dueDate);

    return filteredEntities
      .sort((a, b) => {
        const da = dueMsOf(a);
        const db = dueMsOf(b);

        // Undated entities sink to the bottom (as they always did).
        if (da === undefined || db === undefined) {
          if (da !== db) return da === undefined ? 1 : -1;
        } else {
          // "Now"-anchored ordering: UPCOMING block first, ascending (the very
          // next send at the top), then the PAST block descending (most recent
          // first). Previously this was a single ascending sort, so a user whose
          // oldest item was a two-month-old failure always opened on a graveyard
          // and had to scroll past it to find their next real send.
          const aFuture = da >= nowMs;
          const bFuture = db >= nowMs;
          if (aFuture !== bFuture) return aFuture ? -1 : 1;
          const dateCompare = aFuture ? da - db : db - da;
          if (dateCompare !== 0) return dateCompare;
        }

        // If dates are equal, use campaign status as tiebreaker
        if (a.type === 'campaign' && b.type === 'campaign') {
          if (a.campaignStatus === 'running' && b.campaignStatus === 'pending') return -1;
          if (a.campaignStatus === 'pending' && b.campaignStatus === 'running') return 1;
        }

        return 0;
      })
      .map((entity) => {
        const dueDateMs = dueMsOf(entity);
        return {
          _id: entity._id,
          type: entity.type,
          isGroup: entity.type === 'message' ? !!entity.isGroup : false,
          dueDateMs,
          // Consumers render the Upcoming/Earlier divider from this flag (they
          // filter the list further, so the boundary must be derived after
          // filtering, not from an index computed here).
          isUpcoming: dueDateMs === undefined ? false : dueDateMs >= nowMs,
        };
      });
  }
)

/**
 * How much the planner is showing versus how much it COULD show. The filter panel needs
 * both to say "showing 11 of 51" — a filtered surface can only report its own length, so
 * the denominator is computed here against the most permissive filter state (a fixed
 * denominator, so the number does not move under the user when they toggle "show past").
 */
export const selectPlannerCounts = createSelector(
  getUserMessagesForPlanner,
  (state: CommonRootState) => state.DBUserMessageReducer,
  (state: CommonRootState) => state.DBCampaignReducer,
  selectPlannerFilters,
  (plannerMessages, userMessageState, campaignState, filters) => {
    // The numerator is what the planner shows — the very list both surfaces render.
    const shownMessages = plannerMessages.length;

    // The denominator must be a genuine SUPERSET of every filter state, or the readout can
    // print "1 of 0". `isPlannerCandidate` is the predicate under the all-open filters, which
    // IS the widest state now that no filter can reveal something the open state hides
    // (pinned by the 2^6 enumeration in plannerFilters.test).
    // No `deleted` clause: a send that went out is a soft-deleted row, and whether a deleted
    // row is history or a cancellation is `isPlannerCandidate`'s call (isSentRecord).
    const totalMessages = userMessagesSelectByQuery(userMessageState, {
      status: { $in: plannerSourceStatuses(PERMISSIVE_PLANNER_FILTERS) },
    }).filter(isPlannerCandidate).length;

    const allCampaigns = campaignsSelectByQuery(campaignState, { campaignStatus: { $in: ['pending', 'running'] } }).length;
    const shownCampaigns = campaignsVisible(filters) ? allCampaigns : 0;

    return { shown: shownMessages + shownCampaigns, total: totalMessages + allCampaigns };
  }
);

/** How many free-tier schedule slots the user is currently using (pending, standalone). */
export const selectScheduleSlotsUsed = createSelector(
  (state: CommonRootState) => state.DBUserMessageReducer,
  (userMessageState) => countScheduleSlotsUsed(userMessageState),
);

export const isOverLimitOfScheduledMessages = createSelector(
  (state: CommonRootState) => state,
  (state: CommonRootState) => {
    // Count ONLY what the user genuinely has scheduled — the same set the
    // scheduled-messages LIST shows them (non-deleted, pending, standalone).
    // The gate and the list must agree, or we show a paywall over an empty list.
    // See free-limit-gate.ts for the bug this fixes (failed messages and campaign
    // children were consuming free slots; 2,787 free users were walled with ZERO
    // pending messages).
    const slotsUsed = countScheduleSlotsUsed(state.DBUserMessageReducer);
    const isEditingExistingMessage = !!state.AddScheduledMessageButtonReducer.message._id;
    const messageLimit = messageLimitSelector(state);
    return isOverScheduleLimit(slotsUsed, messageLimit, isEditingExistingMessage);
  }
);

export const selectUserMessagesInRedis = (state: CommonRootState) =>
  state.UserMessageReducer?.userMessagesInRedis;


export const selectScheduledMessagesInRedisAndInDBSelector = createSelector(
  [
    (state: CommonRootState) => state,
    selectUserMessagesInRedis,
    (_, campaignId: string) => campaignId
  ],
  (state: CommonRootState, userMessagesInRedis, campaignId) => {
    const userMessagesInRedisByCampaignId = userMessagesInRedis?.[campaignId] ?? [];
    const userMessagesInDb = getMessagesByCampaignId(state, campaignId);
    const stuckMessages = getStuckMessagesByCampaignId(state, campaignId);

    // Reconcile against the LIVE message-log store: on send the backend deletes
    // the user-message + creates a log, but that deletion doesn't always reach
    // this client live (only a refresh rebuilds the store). The log CREATE does
    // arrive live — it's what updates the "Sent" tab — so dropping any
    // user-message a log already reports as sent keeps "waiting to be sent" in
    // lockstep with "sent" without a refresh. (Failed/queued logs are kept, so
    // failed messages stay retryable here.)
    const campaignLogs = Object.values(state.DBMessageLogReducer.entities)
      .filter((log): log is NonNullable<typeof log> => !!log && (log as any).campaignId === campaignId);
    const sentIds = sentMessageIdsFromLogs(campaignLogs as any);

    const newMessagesListInDb = excludeSentFromWaiting(userMessagesInDb, sentIds).map((message) => {
      if (stuckMessages.some(stuckMessage => stuckMessage._id === message._id)) {
        return null;
      }
      return {
        ...message,
        createdAt: new Date(message.createdAt?.toString() ?? ''),
        status: "Waiting In DB",

      }
    }).filter(Boolean);

    const mergedMessages = [...userMessagesInRedisByCampaignId, ...newMessagesListInDb];

    return mergedMessages;
  }
);
